<script setup lang="ts">
  import { Typo, Button } from '@travelata-libs/ui-kit';
  import { computed, onMounted, watch } from 'vue';
  import SwitchCityModal from './SwitchCityModal.vue';
  import { SWITCH_CITY_TEXTS } from '../config/texts';
  import { buildCityUrl } from '../lib/buildCityUrl';
  import type { SwitchCity } from '../model/types';
  import { useSwitchCity } from '../model/useSwitchCity';

  interface SwitchCityFieldProps {
    currentCity?: SwitchCity | null;
    cities?: SwitchCity[];
    redirect?: boolean;
    buttonType?: 'dashed' | 'link';
  }

  const props = withDefaults(defineProps<SwitchCityFieldProps>(), {
    currentCity: null,
    cities: () => [],
    redirect: true,
    buttonType: 'dashed',
  });

  interface SwitchCityFieldEmits {
    (e: 'update:currentCity', city: SwitchCity): void;
    (e: 'change', city: SwitchCity): void;
    (e: 'navigate', url: string): void;
  }

  const emit = defineEmits<SwitchCityFieldEmits>();

  const core = useSwitchCity({ initialCities: props.cities ?? [] });

  const controlLabel = computed<string>(() => core.label.value || SWITCH_CITY_TEXTS.emptyCity);

  const onOpen = (): void => {
    core.open();
  };

  const onSelect = (city: SwitchCity): void => {
    core.selectCity(city);

    const hasWindow = typeof window !== 'undefined';

    const targetUrl = hasWindow ? buildCityUrl(city.url, window.location) : city.url;

    emit('update:currentCity', city);
    emit('change', city);
    emit('navigate', targetUrl);

    if (props.redirect && hasWindow) {
      window.location.assign(targetUrl);
    }
  };

  const onClose = (): void => {
    core.close();
  };

  watch(
    () => props.cities,
    (list) => core.setCities(list ?? []),
  );

  watch(
    () => props.currentCity,
    (city) => core.restore(city ?? null),
  );

  onMounted(() => {
    core.restore(props.currentCity ?? null);
  });
</script>

<template>
  <div
    class="switch-city-field"
    data-testid="switch-city-field"
  >
    <Button
      v-if="buttonType === 'dashed'"
      appearance="sub-primary"
      type="dash-link"
      @click="onOpen"
    >
      {{ controlLabel }}
    </Button>

    <Button
      v-else
      type="link"
      appearance="sub-primary"
      icon-left="pin"
      size="s"
      @click="onOpen"
    >
      <Typo variant="body-xl-400">{{ controlLabel }}</Typo>
    </Button>

    <SwitchCityModal
      :visible="core.modalOpen.value"
      :grouped="core.grouped.value"
      :query="core.query.value"
      :selected-id="core.currentCity.value?.id"
      :has-results="core.hasResults.value"
      @update:query="core.setQuery"
      @select="onSelect"
      @close="onClose"
    />
  </div>
</template>
